import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DocumentIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';

interface ClientFileSummary {
  id: number;
  name: string;
  fileCount: number;
  lastFileUpdatedAt: string | null;
}

interface ClientFilesResponse {
  clients: ClientFileSummary[];
  totalPages: number;
  currentPage: number;
  totalCount: number;
}

const CLIENT_STATUS_FILTERS = {
  all: 'Все клиенты',
  active: 'Активные',
  inactive: 'Неактивные'
};

const formatDateTime = (value: string | null) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(value));
};

const ClientFilesList: React.FC = () => {
  const { can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState<ClientFileSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');

  const fetchClients = useCallback(async (page = currentPage, search = searchTerm, status = statusFilter) => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', '50');
    if (search) params.set('search', search);
    if (status !== 'all') params.set('status', status);
    setSearchParams(params, { replace: true });

    try {
      const response = await axios.get<ClientFilesResponse>('/client-files/clients', { params });
      setClients(response.data.clients);
      setCurrentPage(response.data.currentPage);
      setTotalPages(response.data.totalPages);
      setTotalCount(response.data.totalCount);
    } catch (err) {
      console.error('Error fetching client files:', err);
      setError('Ошибка при загрузке списка клиентов');
      setClients([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter, setSearchParams]);

  useEffect(() => {
    fetchClients(currentPage, searchTerm, statusFilter);
  }, [fetchClients, currentPage, searchTerm, statusFilter]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (!can('files.view')) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-red-600">У вас нет прав доступа к файлам</p>
      </div>
    );
  }

  if (loading && clients.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <PageHeader
        title="Файлы"
        subtitle={`Клиентов найдено: ${totalCount}`}
      />

      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded-md mb-4">
          {error}
        </div>
      )}

      <div className="mb-6 flex flex-wrap gap-4 items-end">
        <div className="flex-grow sm:flex-grow-0 sm:w-80">
          <label htmlFor="client-file-search" className="form-label">Поиск по имени клиента</label>
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              id="client-file-search"
              type="text"
              className="form-input pl-10"
              placeholder="Введите имя клиента..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </div>
        <div className="flex-grow sm:flex-grow-0 sm:w-48">
          <label htmlFor="client-file-status" className="form-label">Статус клиента</label>
          <select
            id="client-file-status"
            className="form-input"
            value={statusFilter}
            onChange={handleStatusChange}
          >
            {Object.entries(CLIENT_STATUS_FILTERS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Имя клиента
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Загружено файлов
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Последние изменения
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Файлы
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && clients.length > 0 && (
                <tr>
                  <td colSpan={4} className="p-4 text-center text-gray-500">
                    <LoadingSpinner /> Загрузка...
                  </td>
                </tr>
              )}
              {!loading && clients.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-gray-500">
                    Клиенты не найдены
                  </td>
                </tr>
              )}
              {!loading && clients.map((client) => (
                <tr key={client.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {client.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 text-right">
                    {client.fileCount}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {formatDateTime(client.lastFileUpdatedAt)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Link
                      to={`/files/clients/${client.id}`}
                      className="inline-flex items-center justify-center rounded-md p-2 text-primary hover:text-primary-dark hover:bg-blue-50"
                      title="Открыть файлы клиента"
                    >
                      <DocumentIcon className="h-5 w-5" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default ClientFilesList;
